/**
 * Validación de RIF / cédula, espejo de `src/utils/rif-validator.util.ts` en la
 * api y de `CustomValidators.validateRif` en signup. Se replica acá porque son
 * repos separados; si cambia el algoritmo hay que tocar los tres.
 */

/** Prefijos que identifican a una persona natural (cédula), no a una empresa. */
const NATURAL_PERSON_TYPES = ['V', 'E'];

/** Peso de cada letra en el cálculo del dígito verificador. */
const LETTER_VALUES: { [key: string]: number } = { V: 4, E: 8, J: 12, P: 16, G: 20 };

const WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];

/** RIF de empresa: 9 dígitos con dígito verificador módulo 11. */
export function validateRif(rifNumbers: string, rifType: string): boolean {
    const numbers = String(rifNumbers || '').trim();
    const type = String(rifType || '')
        .trim()
        .toUpperCase();

    if (!/^\d{9}$/.test(numbers)) {
        return false;
    }

    const letterWeight = LETTER_VALUES[type];
    if (letterWeight === undefined) {
        return false;
    }

    const total = numbers
        .slice(0, 8)
        .split('')
        .reduce((acc, digit, index) => acc + Number(digit) * WEIGHTS[index], letterWeight);

    const value = 11 - (total % 11);
    const calculatedVerifier = value < 10 ? value : 0;

    return calculatedVerifier === parseInt(numbers.slice(-1), 10);
}

/**
 * Empresas (J/G/P) exigen el RIF completo con dígito verificador; personas
 * naturales (V/E) pueden escribir la cédula pelada, que no lo lleva.
 */
export function isValidTaxId(rifType: string, rifNumbers: string): boolean {
    const type = String(rifType || '')
        .trim()
        .toUpperCase();
    const numbers = String(rifNumbers || '').trim();

    if (NATURAL_PERSON_TYPES.includes(type)) {
        return /^\d{7,9}$/.test(numbers);
    }

    return validateRif(numbers, type);
}
